import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Bars3Icon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MyVendorCardActionMenu from "./MyVendorCardActionMenu";
import MyVendorLastQualifiedDateButton, {
  resolveLastQualifiedRequest,
} from "./MyVendorLastQualifiedDateButton";
import MyVendorTableRequestCell from "./MyVendorTableRequestCell";
import MyVendorTableTagsCell from "./MyVendorTableTagsCell";
import MyVendorsStickyScrollbar from "./MyVendorsStickyScrollbar";
import useMyVendorsStickyScrollbarSync from "./useMyVendorsStickyScrollbarSync";
import { MyVendorRiskScoreBadgeWithDots } from "./MyVendorRiskScoreBadge";
import ServiceCategoryPill from "../ServiceCategoryPill";
import { vendorTypeColors } from "../../utils/constants";
import { StickyActionsTd, StickyActionsTh } from "../../components/StickyActionsCells";
import {
  clickableRowClass,
  shouldIgnoreRowClick,
  stickyActionColumnStyle,
} from "../../utils/gridTableUx";
import {
  getVendorDisplayType,
  hasVendorRiskSummary,
  isManualOnlyVendor,
  LAST_QUALIFIED_DATE_COLUMN_ENABLED,
  RISK_SUMMARY_COLUMN_ENABLED,
} from "../../utils/myVendorsConstants";
import { applyManualSortOrder } from "../../utils/myVendorListViewUtils";
import { sortVendorsByNameAscending } from "../../utils/myVendorSortUtils";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function parseLastQualifiedTimestamp(vendor) {
  const raw = vendor?.lastQualifiedRequest?.date || vendor?.lastQualifiedDate;
  if (!raw) return null;
  try {
    const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  } catch {
    return null;
  }
}

function compareStrings(a, b, direction) {
  return direction * String(a || "").toLowerCase().localeCompare(String(b || "").toLowerCase());
}

function compareNumbers(a, b, direction, zeroLast = false) {
  if (zeroLast) {
    if (a === 0 && b === 0) return 0;
    if (a === 0) return 1;
    if (b === 0) return -1;
  }
  if (a < b) return direction === 1 ? -1 : 1;
  if (a > b) return direction === 1 ? 1 : -1;
  return 0;
}

function GxpProfileCell({ applicableOnlyGxPs = [], demonstratedGxPs = [] }) {
  if (!applicableOnlyGxPs.length && !demonstratedGxPs.length) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {applicableOnlyGxPs.map((gxp) => (
        <span
          key={`applicable-${gxp}`}
          className="inline-flex items-center rounded-md bg-slate-200 px-2 py-1 text-xs font-medium text-slate-600"
        >
          {gxp}
        </span>
      ))}
      {applicableOnlyGxPs.length > 0 && demonstratedGxPs.length > 0 ? (
        <span className="mx-0.5 inline-block h-4 w-px bg-gray-300" aria-hidden="true" />
      ) : null}
      {demonstratedGxPs.map((gxp) => (
        <span
          key={`demonstrated-${gxp}`}
          className="inline-flex items-center rounded-md bg-slate-600 px-2 py-1 text-xs font-medium text-white"
        >
          {gxp}
        </span>
      ))}
    </div>
  );
}

/** Explicit widths so table-fixed distributes leftover space evenly after hidden columns. */
const COL = {
  name: "w-[180px] min-w-[180px]",
  type: "w-[150px] min-w-[150px]",
  location: "w-[150px] min-w-[150px]",
  riskSummary: "w-[150px] min-w-[150px]",
  gxp: "w-[130px] min-w-[130px]",
  services: "w-[180px] min-w-[180px]",
  libraryDocs: "w-[160px] min-w-[160px]",
  requests: "w-[280px] min-w-[280px]",
  lastQualified: "w-[140px] min-w-[140px]",
  tags: "w-[180px] min-w-[180px]",
  addedBy: "w-[130px] min-w-[130px]",
  dateAdded: "w-[110px] min-w-[110px]",
};

function ServicesCell({ serviceCategories = [] }) {
  if (!serviceCategories.length) return <span className="text-gray-400">—</span>;
  const visible = serviceCategories.slice(0, 3);
  const overflow = serviceCategories.length - 3;
  return (
    <div className="flex min-w-0 w-full flex-wrap items-center gap-1">
      {visible.map((service) => (
        <ServiceCategoryPill key={service} name={service} />
      ))}
      {overflow > 0 ? (
        <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600">
          +{overflow} more
        </span>
      ) : null}
    </div>
  );
}

function LibraryDocumentsCell({ rfiCount = 0, assessmentCount = 0, hideVqq = false }) {
  return (
    <div className="space-y-1">
      {!hideVqq && (
        <div className="flex items-center">
          <CheckCircleIcon className="h-5 w-5 text-indigo-700" aria-hidden="true" />
          <span className="inline-block pl-2">
            {rfiCount} VQQ{rfiCount === 1 ? "" : "s"}
          </span>
        </div>
      )}
      <div className="flex items-center">
        <CheckBadgeIcon className="h-5 w-5 text-rose-700" aria-hidden="true" />
        <span className="inline-block pl-2">
          {assessmentCount} Audit{assessmentCount === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
}

function getVendorTagSortKey(vendor) {
  const tags = vendor?.tags;
  if (Array.isArray(tags) && tags.length) return String(tags[0]).toLowerCase();
  if (typeof tags === "string" && tags.trim()) {
    return tags.split(",")[0].trim().toLowerCase();
  }
  return "";
}

function parseDateAddedTimestamp(vendor) {
  const raw = vendor?.dateAdded;
  if (!raw) return null;
  const parsed = Date.parse(String(raw));
  return Number.isNaN(parsed) ? null : parsed;
}

function VendorListRowCells({
  vendor,
  rowNumber,
  sponsorId,
  isSelected,
  isManual,
  dragListeners,
  isHovering,
  isDragging,
  expandedRowIds,
  toggleRowExpansion,
  onToggleVendorSelection,
  onNavigateToProfile,
  onRequestAction,
  onLastQualifiedClick,
  onVendorAction,
  onRemoveFromList,
  showRemoveFromList,
  canRemoveFromList,
  onAddToList,
  isBookmarked,
  isVendorActionPending,
  hideVqq,
}) {
  const vendorId = String(vendor.id);
  const isRowExpanded = expandedRowIds.has(vendorId);
  const applicableOnlyGxPs = (vendor.applicableGxPs || []).filter(
    (gxp) => !(vendor.demonstratedGxPs || []).includes(gxp)
  );
  const demonstratedGxPs = vendor.demonstratedGxPs || [];
  const serviceCategories =
    vendor.serviceCategories?.length > 0
      ? vendor.serviceCategories
      : (vendor.provider?.provider_capabilities || [])
          .map((cap) => cap.rfi_name || cap.name)
          .filter(Boolean);

  return (
    <>
      <td
        className="relative w-12 cursor-move bg-gray-50 px-4 py-4 align-top"
        {...dragListeners}
      >
        {isSelected ? <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" /> : null}
        {isHovering && !isDragging ? (
          <>
            <div className="pointer-events-none absolute left-1/2 top-3 translate-x-[10px] transform">
              <Bars3Icon className="h-4 w-4 text-slate-400 opacity-60" />
            </div>
            <div className="pointer-events-none absolute bottom-3 left-1/2 translate-x-[10px] transform">
              <Bars3Icon className="h-4 w-4 text-slate-400 opacity-60" />
            </div>
          </>
        ) : null}
        <div className="flex items-center justify-center" onClick={(event) => event.stopPropagation()}>
          <div className="group grid size-5 grid-cols-1">
            <input
              type="checkbox"
              className="col-start-1 row-start-1 size-5 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              checked={isSelected}
              onChange={() => onToggleVendorSelection?.(vendor)}
            />
            <svg
              viewBox="0 0 16 16"
              fill="white"
              className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:checked]:opacity-100"
              aria-hidden="true"
            >
              <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
            </svg>
          </div>
        </div>
      </td>
      <td
        className="relative w-10 cursor-move bg-gray-50 px-2 py-4 align-top text-sm text-gray-500"
        {...dragListeners}
      >
        {rowNumber}
      </td>
      <td
        className={classNames(COL.name, "cursor-pointer px-4 py-4 align-top text-sm")}
        onClick={() => onNavigateToProfile(vendor)}
      >
        <span className="whitespace-normal break-words text-sm font-semibold text-gray-900 hover:text-indigo-600">
          {vendor.name}
        </span>
      </td>
      {RISK_SUMMARY_COLUMN_ENABLED ? (
        <td className={classNames(COL.riskSummary, "px-4 py-4 align-top text-sm")}>
          {!isManual && hasVendorRiskSummary(vendor) ? (
            <MyVendorRiskScoreBadgeWithDots
              score={vendor.riskSummary.score}
              rating={vendor.riskSummary.rating}
              size="sm"
              showRatingText
              showPercentage
            />
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </td>
      ) : null}
      <td className={classNames(COL.type, "px-4 py-4 align-top text-sm text-gray-500")}>
        <span className="flex items-start gap-2">
          {vendor.type && vendorTypeColors[vendor.type] ? (
            <span className={classNames("mt-1.5 h-2 w-2 flex-shrink-0 rounded-full", vendorTypeColors[vendor.type])} />
          ) : null}
          <span className="min-w-0 whitespace-normal break-words">{getVendorDisplayType(vendor.type)}</span>
        </span>
      </td>
      <td className={classNames(COL.location, "px-4 py-4 align-top text-sm text-gray-700")}>
        {vendor.primaryLocation ? (
          <span className="whitespace-normal break-words">{vendor.primaryLocation}</span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className={classNames(COL.gxp, "px-4 py-4 align-top text-sm")}>
        <GxpProfileCell applicableOnlyGxPs={applicableOnlyGxPs} demonstratedGxPs={demonstratedGxPs} />
      </td>
      <td className={classNames(COL.services, "px-4 py-4 align-top text-sm")}>
        <ServicesCell serviceCategories={serviceCategories} />
      </td>
      <td className={classNames(COL.libraryDocs, "px-4 py-4 align-top text-sm")}>
        <LibraryDocumentsCell rfiCount={vendor.rfiCount || 0} assessmentCount={vendor.assessmentCount || 0} hideVqq={hideVqq} />
      </td>
      <td className={classNames(COL.requests, "px-4 py-4 align-top text-sm")}>
        <MyVendorTableRequestCell
          items={vendor.myRequests || []}
          emptyLabel="No new requests"
          onClickItem={onRequestAction}
          expanded={isRowExpanded}
          inverted={false}
          onToggleExpand={() => toggleRowExpansion(vendorId)}
        />
      </td>
      <td className={classNames(COL.requests, "px-4 py-4 align-top text-sm")}>
        <MyVendorTableRequestCell
          items={vendor.libraryRequests || []}
          emptyLabel="No library requests"
          onClickItem={onRequestAction}
          expanded={isRowExpanded}
          inverted
          onToggleExpand={() => toggleRowExpansion(vendorId)}
        />
      </td>
      {LAST_QUALIFIED_DATE_COLUMN_ENABLED ? (
        <td className={classNames(COL.lastQualified, "px-4 py-4 align-top text-sm text-gray-700")}>
          <MyVendorLastQualifiedDateButton
            vendor={vendor}
            onClick={onLastQualifiedClick}
          />
        </td>
      ) : null}
      <td className={classNames(COL.tags, "px-4 py-4 align-top text-sm")}>
        <MyVendorTableTagsCell providerId={vendor.id} sponsorId={sponsorId} />
      </td>
      <td className={classNames(COL.addedBy, "px-4 py-4 align-top text-sm text-gray-900")}>
        <span className="whitespace-normal break-words">{vendor.addedBy || "—"}</span>
      </td>
      <td className={classNames(COL.dateAdded, "px-4 py-4 align-top text-sm text-gray-500")}>
        {vendor.dateAdded || "—"}
      </td>
      <StickyActionsTd selected={isSelected}>
        <MyVendorCardActionMenu
          vendor={vendor}
          onAction={onVendorAction}
          showRemoveFromList={
            showRemoveFromList && (canRemoveFromList ? canRemoveFromList(vendor) : true)
          }
          onRemoveFromList={onRemoveFromList}
          onAddToList={onAddToList}
          isBookmarked={isBookmarked?.(vendor.id)}
          isBookmarkPending={isVendorActionPending?.(vendor.id, "bookmark")}
          isHidePending={isVendorActionPending?.(vendor.id, "hide")}
          isUnhidePending={isVendorActionPending?.(vendor.id, "unhide")}
        />
      </StickyActionsTd>
    </>
  );
}

function SortableRow({ id, children, onRowClick, isSelected = false }) {
  const [isHovering, setIsHovering] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: false,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.2 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={(e) => {
        if (
          shouldIgnoreRowClick(e.target, '.cursor-move')
        ) {
          return;
        }
        onRowClick?.(e);
      }}
      className={clickableRowClass({ selected: isSelected })}
    >
      {typeof children === "function"
        ? children({ listeners, isHovering, isDragging })
        : children}
    </tr>
  );
}

/** Within-list vendor table — prototype column layout + manual sort drag-and-drop. */
export default function VendorListViewGrid({
  vendors = [],
  sponsorId = null,
  isManualSort = false,
  manualSortOrder = [],
  defaultSortOrder = [],
  onReorderVendors,
  onColumnSort,
  selectedVendorIds = new Set(),
  onToggleVendorSelection,
  allFilteredSelected = false,
  someFilteredSelected = false,
  onSelectAllFiltered,
  onVendorAction,
  onRequestAction,
  onRemoveFromList,
  showRemoveFromList = false,
  canRemoveFromList,
  onAddToList,
  isBookmarked,
  isVendorActionPending,
  hideVqq = false,
}) {
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedRowIds, setExpandedRowIds] = useState(() => new Set());
  const [activeId, setActiveId] = useState(null);

  const checkboxRef = useRef(null);
  const tableContainerRef = useRef(null);
  const stickyScrollbarRef = useRef(null);
  const [isScrollbarSticky, setIsScrollbarSticky] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleSort = useCallback(
    (column) => {
      onColumnSort?.();
      if (sortColumn === column) {
        if (sortDirection === "asc") setSortDirection("desc");
        else {
          setSortColumn(null);
          setSortDirection("asc");
        }
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [onColumnSort, sortColumn, sortDirection]
  );

  const toggleRowExpansion = useCallback((vendorId) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  }, []);

  const handleNavigateToProfile = useCallback(
    (vendor) => {
      if (vendor?.id) navigate(`/provider/${vendor.id}/overview`);
    },
    [navigate]
  );

  const handleLastQualifiedClick = useCallback(
    (vendor) => {
      const fullRequest = resolveLastQualifiedRequest(vendor);
      if (!fullRequest) return;
      onRequestAction?.(fullRequest);
    },
    [onRequestAction]
  );

  const sortedVendors = useMemo(() => {
    let data = [...vendors];
    if (isManualSort && manualSortOrder.length > 0) {
      data = applyManualSortOrder(data, manualSortOrder);
    } else if (sortColumn) {
      const direction = sortDirection === "desc" ? -1 : 1;
      data.sort((a, b) => {
        switch (sortColumn) {
          case "name":
            return compareStrings(a.name, b.name, direction);
          case "riskSummary": {
            const av =
              hasVendorRiskSummary(a) && typeof a.riskSummary?.score === "number"
                ? a.riskSummary.score
                : 0;
            const bv =
              hasVendorRiskSummary(b) && typeof b.riskSummary?.score === "number"
                ? b.riskSummary.score
                : 0;
            if (av === 0 && bv === 0) return 0;
            if (av === 0) return 1;
            if (bv === 0) return -1;
            return direction === 1 ? bv - av : av - bv;
          }
          case "type":
            return compareStrings(getVendorDisplayType(a.type), getVendorDisplayType(b.type), direction);
          case "primaryLocation":
            return compareStrings(a.primaryLocation, b.primaryLocation, direction);
          case "myRequests":
            return compareNumbers(a.myRequests?.length || 0, b.myRequests?.length || 0, direction === 1 ? -1 : 1, true);
          case "libraryRequests":
            return compareNumbers(
              a.libraryRequests?.length || 0,
              b.libraryRequests?.length || 0,
              direction === 1 ? -1 : 1,
              true
            );
          case "lastQualified": {
            const av = parseLastQualifiedTimestamp(a);
            const bv = parseLastQualifiedTimestamp(b);
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            return direction * (av - bv);
          }
          case "tags": {
            const aVal = getVendorTagSortKey(a);
            const bVal = getVendorTagSortKey(b);
            if (!aVal && !bVal) return 0;
            if (!aVal) return 1;
            if (!bVal) return -1;
            return compareStrings(aVal, bVal, direction);
          }
          case "addedBy":
            return compareStrings(a.addedBy, b.addedBy, direction);
          case "dateAdded": {
            const av = parseDateAddedTimestamp(a);
            const bv = parseDateAddedTimestamp(b);
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            return direction * (av - bv);
          }
          default:
            return 0;
        }
      });
    } else if (defaultSortOrder.length > 0) {
      data = applyManualSortOrder(data, defaultSortOrder);
    } else {
      data = sortVendorsByNameAscending(data);
    }
    return data;
  }, [vendors, isManualSort, manualSortOrder, defaultSortOrder, sortColumn, sortDirection]);

  const sortableIds = useMemo(
    () => sortedVendors.map((vendor) => String(vendor.id)),
    [sortedVendors]
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over || active.id === over.id) return;
      const oldIndex = sortedVendors.findIndex((v) => String(v.id) === String(active.id));
      const newIndex = sortedVendors.findIndex((v) => String(v.id) === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(sortedVendors, oldIndex, newIndex);
      onReorderVendors?.(reordered.map((vendor) => String(vendor.id)));
    },
    [onReorderVendors, sortedVendors]
  );

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <ChevronUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />;
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="ml-1 h-4 w-4 text-indigo-600" />
    ) : (
      <ChevronDownIcon className="ml-1 h-4 w-4 text-indigo-600" />
    );
  };

  const SortHeader = ({ column, children, className = "" }) => (
    <th scope="col" className={classNames("px-4 py-3.5 text-left text-sm font-semibold text-gray-900", className)}>
      <button type="button" className="inline-flex items-center group" onClick={() => handleSort(column)}>
        <span>{children}</span>
        {renderSortIcon(column)}
      </button>
    </th>
  );

  const headerChecked = allFilteredSelected;
  const headerIndeterminate = someFilteredSelected && !allFilteredSelected;

  useLayoutEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = headerIndeterminate;
  }, [headerIndeterminate, headerChecked]);

  useMyVendorsStickyScrollbarSync(
    tableContainerRef,
    stickyScrollbarRef,
    vendors.length > 0,
    sortedVendors.length
  );

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const tableContainer = tableContainerRef.current;
        if (!tableContainer) return;
        const rect = tableContainer.getBoundingClientRect();
        const tableBottom = rect.bottom;
        const windowHeight = window.innerHeight;
        if (isScrollbarSticky) {
          if (tableBottom <= windowHeight - 8) setIsScrollbarSticky(false);
        } else if (tableBottom > windowHeight + 100) {
          setIsScrollbarSticky(true);
        }
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [vendors, sortedVendors.length, isScrollbarSticky]);

  if (!vendors.length) {
    return (
      <p className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        No vendors match the current search or filters.
      </p>
    );
  }

  const activeVendor = activeId
    ? sortedVendors.find((vendor) => String(vendor.id) === String(activeId))
    : null;

  const rowProps = {
    sponsorId,
    expandedRowIds,
    toggleRowExpansion,
    onToggleVendorSelection,
    onNavigateToProfile: handleNavigateToProfile,
    onRequestAction,
    onLastQualifiedClick: handleLastQualifiedClick,
    onVendorAction,
    onRemoveFromList,
    showRemoveFromList,
    canRemoveFromList,
    onAddToList,
    isBookmarked,
    isVendorActionPending,
    hideVqq,
  };

  return (
    <>
      <style>{`
        .vendor-list-hide-scrollbar::-webkit-scrollbar { display: none; }
        .vendor-list-hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(event) => setActiveId(event.active.id)}
        onDragEnd={handleDragEnd}
      >
        <div className="flow-root w-full min-w-0 max-w-full">
          <div
            className={classNames(
              "w-full min-w-0 max-w-full",
              isScrollbarSticky ? "" : "rounded-xl border border-gray-200 bg-white shadow-sm"
            )}
          >
            <div
              ref={tableContainerRef}
              className={classNames(
                "vendor-list-hide-scrollbar w-full min-w-0 max-w-full overflow-x-scroll bg-white",
                isScrollbarSticky
                  ? "mt-0 rounded-xl border border-gray-200 shadow-sm"
                  : "rounded-t-xl border-b-0"
              )}
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <div className="min-w-full py-0 align-middle">
              <table className="w-full min-w-[2070px] table-fixed divide-y divide-gray-200 [&_tbody_td]:h-px [&_tbody_td]:align-top">
                <colgroup>
                  <col className="w-12" />
                  <col className="w-10" />
                  <col className={COL.name} />
                  {RISK_SUMMARY_COLUMN_ENABLED ? (
                    <col className={COL.riskSummary} />
                  ) : null}
                  <col className={COL.type} />
                  <col className={COL.location} />
                  <col className={COL.gxp} />
                  <col className={COL.services} />
                  <col className={COL.libraryDocs} />
                  <col className={COL.requests} />
                  <col className={COL.requests} />
                  {LAST_QUALIFIED_DATE_COLUMN_ENABLED ? (
                    <col className={COL.lastQualified} />
                  ) : null}
                  <col className={COL.tags} />
                  <col className={COL.addedBy} />
                  <col className={COL.dateAdded} />
                  <col style={stickyActionColumnStyle()} />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="w-12 bg-gray-50 px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <div className="group grid size-5 grid-cols-1">
                        <input
                          ref={checkboxRef}
                          type="checkbox"
                          className="col-start-1 row-start-1 size-5 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                          checked={headerChecked}
                          onChange={onSelectAllFiltered}
                        />
                        <svg
                          viewBox="0 0 16 16"
                          fill="white"
                          className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:checked]:opacity-100"
                          aria-hidden="true"
                        >
                          <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                        </svg>
                        <svg
                          viewBox="0 0 16 16"
                          fill="white"
                          className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:indeterminate]:opacity-100"
                          aria-hidden="true"
                        >
                          <path d="M4 8h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                    </th>
                    <th scope="col" className="w-10 bg-gray-50 px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <span className="text-gray-400">#</span>
                    </th>
                    <SortHeader column="name" className={COL.name}>
                      Name
                    </SortHeader>
                    {RISK_SUMMARY_COLUMN_ENABLED ? (
                      <SortHeader column="riskSummary" className={COL.riskSummary}>
                        Risk Summary
                      </SortHeader>
                    ) : null}
                    <SortHeader column="type" className={COL.type}>
                      Type
                    </SortHeader>
                    <SortHeader column="primaryLocation" className={COL.location}>
                      Primary Location
                    </SortHeader>
                    <th scope="col" className={classNames(COL.gxp, "px-4 py-3.5 text-left text-sm font-semibold text-gray-900")}>
                      GxP Profile
                    </th>
                    <th scope="col" className={classNames(COL.services, "px-4 py-3.5 text-left text-sm font-semibold text-gray-900")}>
                      Services
                    </th>
                    <th scope="col" className={classNames(COL.libraryDocs, "px-4 py-3.5 text-left text-sm font-semibold text-gray-900")}>
                      Library Documents
                    </th>
                    <SortHeader column="myRequests" className={COL.requests}>
                      My Requests
                    </SortHeader>
                    <SortHeader column="libraryRequests" className={COL.requests}>
                      Library Requests
                    </SortHeader>
                    {LAST_QUALIFIED_DATE_COLUMN_ENABLED ? (
                      <SortHeader column="lastQualified" className={COL.lastQualified}>
                        Last Qualified Date
                      </SortHeader>
                    ) : null}
                    <SortHeader column="tags" className={COL.tags}>
                      Custom Tags
                    </SortHeader>
                    <SortHeader column="addedBy" className={COL.addedBy}>
                      Added By
                    </SortHeader>
                    <SortHeader column="dateAdded" className={COL.dateAdded}>
                      Date Added
                    </SortHeader>
                    <StickyActionsTh tone="gray" />
                  </tr>
                </thead>
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {sortedVendors.map((vendor, index) => {
                      const vendorId = String(vendor.id);
                      const isSelected = selectedVendorIds.has(vendorId);
                      const isManual = isManualOnlyVendor(vendor);
                      return (
                        <SortableRow
                          key={vendorId}
                          id={vendorId}
                          isSelected={isSelected}
                          onRowClick={() => handleNavigateToProfile(vendor)}
                        >
                          {({ listeners, isHovering, isDragging }) => (
                            <VendorListRowCells
                              vendor={vendor}
                              rowNumber={index + 1}
                              isSelected={isSelected}
                              isManual={isManual}
                              dragListeners={listeners}
                              isHovering={isHovering}
                              isDragging={isDragging}
                              {...rowProps}
                            />
                          )}
                        </SortableRow>
                      );
                    })}
                  </tbody>
                </SortableContext>
              </table>
              </div>
            </div>
          </div>
          <MyVendorsStickyScrollbar
            scrollRef={stickyScrollbarRef}
            isScrollbarSticky={isScrollbarSticky}
          />
        </div>

        <DragOverlay dropAnimation={null}>
          {activeVendor ? (
            <div className="opacity-70">
              <table className="min-w-[2070px] table-fixed divide-y divide-gray-200 [&_tbody_td]:h-px [&_tbody_td]:align-top">
                <tbody>
                  <tr className="border border-indigo-300/40 bg-white shadow-xl">
                    <VendorListRowCells
                      vendor={activeVendor}
                      rowNumber={
                        sortedVendors.findIndex((v) => String(v.id) === String(activeVendor.id)) + 1
                      }
                      isSelected={selectedVendorIds.has(String(activeVendor.id))}
                      isManual={isManualOnlyVendor(activeVendor)}
                      dragListeners={{}}
                      isHovering={false}
                      isDragging={false}
                      {...rowProps}
                    />
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}
